import { createClientSharedComposable, getStore } from '@global-store/store'
import { useCartStore } from '@global-store/stores/cart/cart'
import { useCheckoutAddressStore } from '@global-store/stores/checkout/address'
import { useCheckoutPaymentStore } from '@global-store/stores/checkout/payment'
import type { CheckoutResponseStore } from '@global-store/stores/checkout/types'
import { useCustomerStore } from '@global-store/stores/customer'
import { useLocationStore } from '@global-store/stores/location'
import { sprykerApiClient } from 'internal/api-client'
import { defineStore } from 'pinia'

import { type Locale } from '@fond-of/locale'
import type {
  CartInterface,
  CheckoutDataAttributes,
  CheckoutDataInterface,
} from '@fond-of/spryker-api'

const defaultIso2Codes: Record<Locale, string> = {
  de: 'DE',
  // Because we do not deliver to any country that has English as its main language, we also use Germany as the default for the English language.
  en: 'DE',
  fr: 'FR',
  da: 'DK',
  es: 'ES',
  it: 'IT',
}

const locationStore = useLocationStore()

export const guessIso2Code = () => defaultIso2Codes[locationStore.currentLocale]

export const useCheckoutResponseStore = createClientSharedComposable(() =>
  defineStore('checkoutResponse', {
    state: (): CheckoutResponseStore => ({
      isProcessing: false,
      response: undefined as CheckoutDataInterface | undefined,
    }),

    actions: {
      setResponse(response: CheckoutDataInterface) {
        this.response = response
      },

      getCheckoutDataAttributes: (cart: CartInterface, iso2Code?: string) => {
        const checkoutAddressStore = useCheckoutAddressStore()
        const checkoutPaymentStore = useCheckoutPaymentStore()
        const customerStore = useCustomerStore()

        if (!cart?.id) {
          return undefined
        }

        const cartItemIds = [...new Set(cart?.cartItems.map(({ id }) => id))]

        const checkoutDataAttributes: CheckoutDataAttributes = {
          idCart: cart?.id,
        }

        if (checkoutPaymentStore.payment) {
          checkoutDataAttributes.payments = [checkoutPaymentStore.payment]
        }

        if (checkoutAddressStore.shippingAddress) {
          checkoutDataAttributes.shippingAddress =
            checkoutAddressStore.shippingAddress
          checkoutDataAttributes.shipment = {
            idShipmentMethod: 1,
          }
          checkoutDataAttributes.shipments = [
            {
              idShipmentMethod: 1,
              items: cartItemIds,
              requestedDeliveryDate: null,
              shippingAddress: checkoutAddressStore.shippingAddress,
            },
          ]
        }

        if (checkoutAddressStore.billingAddress) {
          checkoutDataAttributes.billingAddress =
            checkoutAddressStore.billingAddress
        }

        if (checkoutAddressStore.billingAddress?.email) {
          checkoutDataAttributes.customer = {
            email: checkoutAddressStore.billingAddress.email,
            firstName: checkoutAddressStore.billingAddress.firstName,
            lastName: checkoutAddressStore.billingAddress.lastName,
          }
        }

        if (customerStore.customer) {
          checkoutDataAttributes.customer = {
            email: customerStore.customer.email ?? '',
            firstName: customerStore.customer.firstName,
            lastName: customerStore.customer.lastName,
          }
        }

        delete checkoutDataAttributes.billingAddress?.country
        delete checkoutDataAttributes.shippingAddress?.country
        delete checkoutDataAttributes.shipments?.[0].shippingAddress.country

        if (!checkoutDataAttributes.shippingAddress) {
          const shippingAddress = {
            address1: 'Teststraße 1',
            city: 'Test',
            firstName: 'Max',
            iso2Code: guessIso2Code(),
            lastName: 'Mustermann',
            zipCode: '12345',
          }

          checkoutDataAttributes.shipments = [
            {
              idShipmentMethod: 1,
              items: cartItemIds,
              requestedDeliveryDate: null,
              shippingAddress,
            },
          ]

          checkoutDataAttributes.shipment = {
            idShipmentMethod: 1,
          }
          checkoutDataAttributes.shippingAddress = shippingAddress
        }

        if (iso2Code && checkoutDataAttributes.shipments?.[0]) {
          checkoutDataAttributes.shipments[0].shippingAddress.iso2Code =
            iso2Code
          checkoutDataAttributes.shippingAddress.iso2Code = iso2Code
        }

        if (iso2Code && checkoutDataAttributes.billingAddress) {
          checkoutDataAttributes.billingAddress.iso2Code = iso2Code
        }

        if (cart.cartItems.length === 0) {
          // Don't send shipping information if cart is empty
          delete checkoutDataAttributes.shipments
          delete checkoutDataAttributes.shippingAddress
        }

        return checkoutDataAttributes
      },

      async updateCheckoutResponse(cart: CartInterface, iso2Code?: string) {
        this.isProcessing = true

        const checkoutDataAttributes = this.getCheckoutDataAttributes(
          cart,
          iso2Code
        )

        if (!checkoutDataAttributes) {
          this.isProcessing = false

          return this.response
        }

        const checkoutResponse = await sprykerApiClient.provideCheckoutData(
          checkoutDataAttributes
        )

        this.response = checkoutResponse
        this.isProcessing = false

        return checkoutResponse
      },

      getPlaceOrderAttributes() {
        const checkoutAddressStore = useCheckoutAddressStore()
        const cartStore = useCartStore()

        if (!cartStore.cart) {
          return undefined
        }

        const checkoutDataAttributes = this.getCheckoutDataAttributes(
          cartStore.cart
        )

        if (!checkoutDataAttributes) {
          return undefined
        }

        if (
          checkoutAddressStore.deliveryToPackingStation &&
          checkoutDataAttributes.shipments?.length
        ) {
          checkoutDataAttributes.shipments[0].shippingAddress.address1 = `Packstation ${checkoutDataAttributes.shipments?.[0].shippingAddress.address1}`
        }

        return checkoutDataAttributes
      },

      getCountryByIso2Code(iso2Code: string) {
        return (
          this.response?.countries.find(
            (country) => country.iso2Code === iso2Code
          ) || {
            idCountry: 0,
            name: iso2Code,
            iso2Code,
          }
        )
      },
    },
  })(getStore())
)
